import React, { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import moment from "moment";

const AudioRecorder = ({ sendDataToServer, onRecordingComplete }) => {
  const dialogRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const recordingRef = useRef(false);
  const recordingTimeRef = useRef(0);
  const audioUrlRef = useRef(null);
  const isPlayingRef = useRef(false);
  const hasRecordingRef = useRef(false);
  const chunksRef = useRef([]);
  const audioFileRef = useRef(null);
  const transcriptRef = useRef("");
  const fullTranscriptRef = useRef("");
  const currentInterimRef = useRef("");
  const errorRef = useRef(null);
  const [, forceUpdate] = useState(0);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  // Waveform data storage for scrolling effect
  const waveformDataRef = useRef([]);
  const animationCounterRef = useRef(0); // Added this missing ref
  const MAX_DATA_POINTS = 300; // Number of points to show on screen

  /* ------------------ SPEECH TO TEXT INIT ------------------ */
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      errorRef.current = "Speech Recognition not supported";
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalText += event.results[i][0].transcript + " ";
        } else {
          interimText += event.results[i][0].transcript;
        }
      }

      if (finalText) {
        fullTranscriptRef.current += finalText;
      }
      currentInterimRef.current = interimText;
      transcriptRef.current = fullTranscriptRef.current + interimText;

      forceUpdate(n => n + 1);
    };

    recognition.onerror = (e) => console.error(e);
    recognitionRef.current = recognition;

    return () => recognition.stop();
  }, []);

  /* ------------------ CANVAS SETUP ------------------ */
  const setupAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (!analyserRef.current) {
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 512; // Reduced for better performance
      analyserRef.current.smoothingTimeConstant = 0.5;
      dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
    }
  }, []);

  const drawScrollingWaveform = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current || !dataArrayRef.current) {
      // If no analyser but we have audio playing, try to set it up
      if (audioRef.current && audioRef.current.src && isPlayingRef.current) {
        if (!audioContextRef.current) {
          setupAudioContext();
        }
      }
      animationRef.current = requestAnimationFrame(drawScrollingWaveform);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    const centerY = height / 2;
    const maxAmplitude = height * 0.45;

    // Clear
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#f8f9fa";
    ctx.fillRect(0, 0, width, height);

    // Get frequency data
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Center line
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.strokeStyle = "#d1d5db";
    ctx.lineWidth = 1;
    ctx.stroke();

    /* ------------------ SLOW GRAPH CONFIG ------------------ */
    const barWidth = 2;          // slim bars
    const barGap = 2;
    const maxBars = Math.floor(width / (barWidth + barGap));

    // 🚀 SPEED CONTROL (bigger = slower)
    const SPEED = 10;

    // Initialize animationCounterRef if not exists
    if (!animationCounterRef.current) {
      animationCounterRef.current = 0;
    }
    animationCounterRef.current += 1;

    // Average amplitude
    let sum = 0;
    const dataLength = dataArrayRef.current.length;
    for (let i = 0; i < dataLength; i++) {
      sum += dataArrayRef.current[i];
    }
    let amplitude = (sum / dataLength) / 100;

    // Noise gate
    if (amplitude < 0.03) amplitude = 0.003;

    // ⏳ Add new bar ONLY every few frames (SLOW)
    if (animationCounterRef.current % SPEED === 0) {
      waveformDataRef.current.push(amplitude);

      // Keep FULL WIDTH graph (no fast scrolling)
      if (waveformDataRef.current.length > maxBars) {
        waveformDataRef.current.shift();
      }
    }

    // Draw FULL graph across canvas
    waveformDataRef.current.forEach((amp, i) => {
      const barHeight = amp * maxAmplitude;
      const x = i * (barWidth + barGap);

      ctx.fillStyle = recordingRef.current ? "#ef4444" : "#3b82f6";

      // Top spike
      ctx.fillRect(x, centerY - barHeight, barWidth, barHeight);

      // Bottom spike
      ctx.fillRect(x, centerY, barWidth, barHeight);
    });

    animationRef.current = requestAnimationFrame(drawScrollingWaveform);
  }, [setupAudioContext]);

  /* ------------------ COMPLETE RESET FUNCTION ------------------ */
  const resetRecorder = useCallback(() => {
    // Stop all ongoing processes
    if (recordingRef.current) {
      mediaRecorderRef.current?.stop();
      recognitionRef.current?.stop();
      recordingRef.current = false;
    }

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    // Stop animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Disconnect audio nodes
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Reset analyser
    analyserRef.current = null;
    dataArrayRef.current = null;

    // Stop audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.ontimeupdate = null;
      audioRef.current.onended = null;
      audioRef.current.onloadedmetadata = null;
    }

    // Revoke object URL
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    // Reset waveform data
    waveformDataRef.current = [];
    animationCounterRef.current = 0; // Reset animation counter

    // Reset all state
    recordingTimeRef.current = 0;
    isPlayingRef.current = false;
    hasRecordingRef.current = false;
    chunksRef.current = [];
    audioFileRef.current = null;
    transcriptRef.current = "";
    fullTranscriptRef.current = "";
    currentInterimRef.current = "";
    errorRef.current = null;
    setPlaybackProgress(0);
    setAudioDuration(0);

    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Draw baseline
      ctx.beginPath();
      ctx.moveTo(0, canvasRef.current.height / 2);
      ctx.lineTo(canvasRef.current.width, canvasRef.current.height / 2);
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    forceUpdate(n => n + 1);
  }, []);

  /* ------------------ START RECORDING ------------------ */
  const startRecording = async () => {
    try {
      // Reset waveform data
      waveformDataRef.current = [];
      animationCounterRef.current = 0;

      transcriptRef.current = "";
      fullTranscriptRef.current = "";
      currentInterimRef.current = "";
      errorRef.current = null;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      streamRef.current = stream;

      // Start audio visualization
      setupAudioContext();

      // Create analyser if it doesn't exist
      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512;
        analyserRef.current.smoothingTimeConstant = 0.5;
        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
      }

      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      // Connect analyser to destination
      sourceRef.current.connect(analyserRef.current);


      // Start waveform animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(drawScrollingWaveform);

      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond: 128000
      });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = e => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;

        let name = `recording_${moment().format("DD_MM_YYYY_hh_mm_a")}.webm`;

        audioFileRef.current = new File([blob], name, { type: "audio/webm" });
        hasRecordingRef.current = true;
        onRecordingComplete?.(audioFileRef.current, url);

        // Create audio element to get duration
        const audio = new Audio(url);
        audio.onloadedmetadata = () => {
          setAudioDuration(audio.duration);
          forceUpdate(n => n + 1);
        };

        stream.getTracks().forEach(t => t.stop());

        // Stop visualization
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (sourceRef.current) {
          sourceRef.current.disconnect();
        }

        // Clear waveform for playback
        waveformDataRef.current = [];
        animationCounterRef.current = 0;

        forceUpdate(n => n + 1);
      };

      recorder.start(100);
      recognitionRef.current?.start();
      recordingRef.current = true;
      recordingTimeRef.current = 0;

      timerRef.current = setInterval(() => {
        recordingTimeRef.current++;
        forceUpdate(n => n + 1);
      }, 1000);

      forceUpdate(n => n + 1);
    } catch (err) {
      errorRef.current = "Microphone access denied or not available";
      console.error("Recording error:", err);
      forceUpdate(n => n + 1);
    }
  };

  /* ------------------ STOP RECORDING ------------------ */
  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingRef.current) {
      mediaRecorderRef.current.stop();
      recognitionRef.current?.stop();
      recordingRef.current = false;

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Keep visualization running for a moment
      setTimeout(() => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      }, 100);

      forceUpdate(n => n + 1);
    }
  };

  const togglePlayback = async () => {
    if (!audioRef.current || !audioUrlRef.current) return;

    if (isPlayingRef.current) {
      audioRef.current.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    } else {
      // Reset waveform data for playback
      waveformDataRef.current = [];
      animationCounterRef.current = 0;

      // Setup audio context for playback visualization
      setupAudioContext();
      if (!sourceRef.current) {
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
      }

      // Start waveform animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(drawScrollingWaveform);

      // Ensure audio is loaded
      if (audioRef.current.readyState < 2) {
        audioRef.current.load();
      }

      audioRef.current.play().catch(err => {
        console.error("Playback error:", err);
        isPlayingRef.current = false;
        forceUpdate(n => n + 1);
      });

      // Update progress during playback
      audioRef.current.ontimeupdate = () => {
        const progress = audioRef.current.duration > 0 ?
          (audioRef.current.currentTime / audioRef.current.duration) * 100 : 0;
        setPlaybackProgress(progress || 0);
        forceUpdate(n => n + 1);
      };

      audioRef.current.onended = () => {
        isPlayingRef.current = false;
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        setPlaybackProgress(0);
        forceUpdate(n => n + 1);
      };
    }

    isPlayingRef.current = !isPlayingRef.current;
    forceUpdate(n => n + 1);
  };

  const deleteRecording = () => {
    resetRecorder();
  };

  const submit = () => {
    if (audioFileRef.current && transcriptRef.current) {
      sendDataToServer?.(audioFileRef.current, transcriptRef.current);
      dialogRef.current.close();
      resetRecorder();
    }
  };

  const closeDialog = () => {
    dialogRef.current.close();
    resetRecorder();
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "00:00";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Handle audio metadata load
  const handleAudioLoaded = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
      forceUpdate(n => n + 1);
    }
  };

  // Split transcript into words for display
  const renderTranscript = () => {
    return (
      <div className="audio-recorder-transcript-box">
        {/* Previous finalized text - fading */}
        {fullTranscriptRef.current && (
          <p className="audio-recorder-transcript-text" style={{ opacity: 0.5, marginBottom: '0.5rem' }}>
            {fullTranscriptRef.current}
          </p>
        )}

        {/* Current speaking text - highlighted */}
        {currentInterimRef.current && (
          <p className="audio-recorder-transcript-text" style={{ color: '#3b82f6', fontWeight: 600 }}>
            {currentInterimRef.current}
          </p>
        )}

        {/* Placeholder when not speaking */}
        {!currentInterimRef.current && !fullTranscriptRef.current && (
          <p className="audio-recorder-transcript-text" style={{ fontStyle: 'italic', opacity: 0.6 }}>
            Listening for speech...
          </p>
        )}
      </div>
    );
  };

  /* ------------------ CLEANUP ------------------ */
  useEffect(() => {
    return () => {
      resetRecorder();
    };
  }, [resetRecorder]);

  return (
    <div className="audio-recorder-container">
      <Button
        icon="pi pi-microphone"
        size="small"
        onClick={() => dialogRef.current.showModal()}
        aria-label="Record Audio"
      />

      <dialog ref={dialogRef} className="audio-recorder-dialog">
        {/* Header */}
        <div className="audio-recorder-header">
          <h2 className="audio-recorder-title">Audio Recorder</h2>
          <p className="audio-recorder-subtitle">Record and transcribe audio</p>
        </div>

        {/* Main Content */}
        <div className="audio-recorder-content">
          {/* Error Message */}
          {errorRef.current && (
            <div className="audio-recorder-error">
              <i className="pi pi-exclamation-triangle"></i>
              <span>{errorRef.current}</span>
            </div>
          )}

          {/* Visualization */}
          <div className="audio-recorder-visualization">
            {recordingRef.current && (
              <div className="audio-recorder-visualization-header">
                <div className="audio-recorder-recording-badge">
                  <span className="audio-recorder-recording-dot"></span>
                  REC
                </div>
              </div>
            )}
            <div className="audio-recorder-waveform-container">
              <canvas
                ref={canvasRef}
                width={468}
                height={120}
                className="audio-recorder-waveform"
              />
            </div>
          </div>

          {/* Live Transcript */}
          {recordingRef.current && (
            <div className="audio-recorder-transcript">
              <div className="audio-recorder-transcript-header">
                <i className="pi pi-comments audio-recorder-transcript-icon"></i>
                <span className="audio-recorder-transcript-label">Live Transcription</span>
                <span className="audio-recorder-transcript-badge">Beta</span>
              </div>
              {renderTranscript()}
            </div>
          )}



          {/* Audio Player */}
          {hasRecordingRef.current && !recordingRef.current && (
            <div className="">
              <audio
                ref={audioRef}
                src={audioUrlRef.current}
                onEnded={() => {
                  isPlayingRef.current = false;
                  if (animationRef.current) {
                    cancelAnimationFrame(animationRef.current);
                  }
                  setPlaybackProgress(0);
                  forceUpdate(n => n + 1);
                }}
                className="mt-3 w-100"
                controls
              />

              <div className="audio-recorder-player-controls">
                <Button
                  icon={isPlayingRef.current ? "pi pi-pause" : "pi pi-play"}
                  onClick={togglePlayback}
                  className="audio-recorder-play-btn"
                  aria-label={isPlayingRef.current ? "Pause" : "Play"}
                  disabled={!audioUrlRef.current}
                />
                <div className="audio-recorder-duration">
                  <span className="audio-recorder-duration-label">Duration</span>
                  <span className="audio-recorder-duration-value">
                    {formatTime(audioDuration)}
                  </span>
                </div>
                <Button
                  icon="pi pi-trash"
                  onClick={deleteRecording}
                  className="audio-recorder-delete-btn"
                  aria-label="Delete Recording"
                />
              </div>
            </div>
          )}
          {/* Final Transcript (after recording ends) */}
          {hasRecordingRef.current && !recordingRef.current && fullTranscriptRef.current && (
            <div className="audio-recorder-final-transcript">
              <div className="audio-recorder-transcript-header">
                <i className="pi pi-file-edit"></i>
                <span className="audio-recorder-transcript-label">Full Transcript</span>
              </div>

              <div className="audio-recorder-transcript-box final">
                <p className="audio-recorder-transcript-text">
                  {fullTranscriptRef.current.trim()}
                </p>
              </div>
            </div>
          )}

          {/* Control Buttons */}
          <div className="audio-recorder-controls ">
            {!recordingRef.current && !hasRecordingRef.current && (
              <div className="audio-recorder-idle">
                <p className="idle-text">Tap to start recording</p>
                <Button
                  icon="pi pi-microphone"
                  rounded
                  onClick={startRecording}
                  className="audio-recorder-record-btn"
                  aria-label="Start Recording"
                />
              </div>
            )}


            {recordingRef.current && (
              <Button
                icon="pi pi-stop"
                rounded
                severity="danger"
                onClick={stopRecording}
                className="audio-recorder-stop-btn"
                aria-label="Stop Recording"
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="audio-recorder-actions">
            <Button
              label="Cancel"
              onClick={closeDialog}
              className="audio-recorder-cancel-btn"
            />
            <Button
              label="Submit"
              onClick={submit}
              disabled={!hasRecordingRef.current}
              className="audio-recorder-submit-btn"
            />
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default AudioRecorder;